import * as React from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import "./CreateDataSource.css";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
} from "@mui/material";
import Breadcrumb from "../../../components/bootstrap/Breadcrumb";
import Button from "../../../components/bootstrap/Button";
import Card, {
  CardActions,
  CardBody,
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
  CardHeader,
  CardLabel,
  CardSubTitle,
  CardTitle,
} from "../../../components/bootstrap/Card";
import Checks from "../../../components/bootstrap/forms/Checks";
import FormGroup from "../../../components/bootstrap/forms/FormGroup";
import Input from "../../../components/bootstrap/forms/Input";
import Select from "../../../components/bootstrap/forms/Select";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../components/ToastComponent";
import { capitalizeAllLetter } from "../common-component/CapitalizeFirstLetter";
import ToastComponent from "../../../components/ToastComponent";
import { useLocation } from "react-router-dom";

type DbTypeObj = {
  dbType: string;
  driverId: { driverName: string; driverType: string }[];
  // add other properties if needed
};

type Driver = {
  id: number;
  driverName: string;
  driverType: string;
};
type DBType = {
  id: number;
  dbType: string;
  driverId: Driver[];
};
type DBFamily = {
  id: number;
  name: string; // This is the DB Family name
  dbTypes: DBType[];
};

export default function CreateDataSource() {
  const token = localStorage.getItem("token");
  const vyu_api_url = process.env.REACT_APP_VYU_API_URL;
  const vyu_service_url = process.env.REACT_APP_VYU_SERVICE_URL;
  const form = useForm<FormValues>();
  const [driverNames, setDriverNames] = React.useState<string[]>([]);
  const [driverTypes, setDriverTypes] = React.useState<string[]>([]);
  const [dbFamily, setDbFamily] = React.useState<DbTypeObj[]>([]);
  const [dbType, setDbType] = React.useState<DbTypeObj[]>([]);
  const [data, setData] = React.useState<string[]>([]);
  const [driverNameList, setDriverNameList] = React.useState<
    { driverName: string; driverType: string }[] | undefined
  >();
  const { handleSubmit } = form;
  const [show, setShow] = React.useState(false);
  const [disabled, setDisabled] = React.useState(true);
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [formData, setFormData] = React.useState<FormValues>({
    name: "",
    url: "",
    userName: "",
    pwd: "",
    driverName: "",
    driverType: "",
    createUser: "",
    dbType: "",
    dbFamily: "",
  });
  const [dbHierarchyData, setDbHierarchyData] = React.useState<DBFamily[]>([]);
  const [dbFamilies, setDbFamilies] = React.useState<string[]>([]);
  const [filteredDbTypes, setFilteredDbTypes] = React.useState<DBType[]>([]);
  const [filteredDriverNames, setFilteredDriverNames] = React.useState<
    Driver[]
  >([]);

  const location = useLocation();

  type FormValues = {
    name: string;
    url: string;
    userName: string;
    pwd: string;
    driverName: string;
    driverType: string;
    createUser: string;
    dbType: string;
    dbFamily: string;
  };
  useEffect(() => {
    // apiCallToGetAllDbTypes();
    apiCallToGetAllDbFamily();
  }, []);

  // Load form data on mount
  useEffect(() => {
    const savedFormData = localStorage.getItem("dataSourceFormData");
    // if (savedFormData) {
    //   setFormData(JSON.parse(savedFormData));
    // }
    if (savedFormData && dbHierarchyData.length > 0) {
      const parsedData: FormValues = JSON.parse(savedFormData);
      setFormData(parsedData);

      // Re-populate dependent dropdowns based on saved data
      if (parsedData.dbFamily) {
        const family = dbHierarchyData.find(
          (f) => f.name === parsedData.dbFamily
        );
        if (family) {
          setFilteredDbTypes(family.dbTypes);
          if (parsedData.dbType) {
            const type = family.dbTypes.find(
              (t) => t.dbType === parsedData.dbType
            );
            if (type) {
              setFilteredDriverNames(type.driverId);
            }
          }
        }
      }
    }
  }, [dbHierarchyData]);

  // Save form data on change
  useEffect(() => {
    localStorage.setItem("dataSourceFormData", JSON.stringify(formData));
  }, [formData]);

  // Clear localStorage if leaving data-source section
  useEffect(() => {
    return () => {
      if (!location.pathname.startsWith("/datasource")) {
        localStorage.removeItem("dataSourceFormData");
      }
    };
  }, [location.pathname]);

  // After loading formData from localStorage, populate driverNames if dbType is present
  // React.useEffect(() => {
  //   if (formData.dbType) {
  //     // Find the selected DB Type object
  //     const selectedDb = dbType.find((db) => db.dbType === formData.dbType);
  //     if (selectedDb) {
  //       setDriverNames(selectedDb.driverId.map((driver) => driver.driverName));
  //       setDriverNameList(selectedDb.driverId);
  //     }
  //   }
  // }, [formData.dbType, dbType]);

  const apiCallToGetAllDbFamily = async () => {
    try {
      const response = await fetch(`${vyu_api_url}/drivers/getAll/dbFamily`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      console.log("Get All DbFamily List:", data);
      if (data.status === "success" && data.obj) {
        console.log("Get All DbFamily List:", data.obj);
        setDbHierarchyData(data.obj);
        setDbFamilies(data.obj.map((family: DBFamily) => family.name));
      } else {
        handleError(data.message || "Error fetching the DB Family List");
      }
    } catch (error) {
      console.error("Error fetching DbFamily:", error);
      handleError("An error occurred while fetching DB families.");
    }
  };

  const apiCallToGetAllDbTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${vyu_api_url}/drivers/getAll/dbTypes`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      console.log("Get All DB types List:", data);
      // Extract only driver types
      const drivers = data.map((driver: { dbType: string }) => driver.dbType);
      console.log("All DB types List:", drivers);
      setData(drivers);
      setDbType(data);
    } catch (error) {
      console.error("Error fetching DB Type:", error);
    }
  };

  const saveDataSource = async () => {
    // console.log(JSON.stringify(formData))
    const token = localStorage.getItem("token");
    formData.name = capitalizeAllLetter(formData.name);
    const formDataObject = {
      ...formData,
      // companyId: 1,
    };
    console.log("formDataObject", formDataObject);
    try {
      const response = await fetch(
        `${vyu_api_url}/datasource/createDatasource`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formDataObject),
        }
      );
      if (response.ok) {
        setSuccessMessage("Data Source created successfully!");
        handleSuccess("Data Source created successfully!");
        handleFullReset();
      } else {
        const data = await response.json();
        handleError(data.message || "Failed to create DataSource!");
      }
    } catch (error) {
      console.error("Error creating widget:", error);
      setErrorMessage("An error occurred while creating DataSource");
    }
  };
  const isValid = () => {
    return (
      formData.driverName !== "" &&
      formData.dbFamily !== "" &&
      formData.userName !== "" &&
      formData.pwd !== "" &&
      formData.url !== ""
    );
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const handleReset = () => {
    setFormData({
      name: "",
      url: "",
      userName: "",
      pwd: "",
      driverName: "",
      driverType: "",
      createUser: "",
      dbType: "",
      dbFamily: "",
    });
    setDisabled(true);
    setShow(false);
  };
  const handleFullReset = () => {
    setFormData({
      name: "",
      url: "",
      userName: "",
      pwd: "",
      driverName: "",
      driverType: "",
      createUser: "",
      dbType: "",
      dbFamily: "",
    });
    setDisabled(true);
    setShow(false);
  };
  const onSubmit = () => {
    saveDataSource();
  };

  const handleTestConnection = async () => {
    // console.log(JSON.stringify(formData))
    const token = localStorage.getItem("token");
    const formDataObject = {
      ...formData,
      // companyId: 1,
    };
    // console.log(formDataObject)
    try {
      const response = await fetch(`${vyu_service_url}/testConnection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formDataObject),
      });
      const data = await response.json();
      setSuccessMessage(data.message);
      setDisabled(false);
      setShow((prev) => !prev);
    } catch (e) {
      console.error(e);
    }
  };

  const handleWarning = (message: string) => {
    showWarningToast(message);
  };
  const handleSuccess = (message: string) => {
    showSuccessToast(message);
  };
  const handleError = (message: string) => {
    showErrorToast(message);
  };
  // const handleDbTypeChange = (event: any) => {
  //   const selectedDbType = event.target.value;

  //   // Update `dbType` in the form data and reset other dependent fields
  //   setFormData((prev) => ({
  //     ...prev,
  //     dbType: selectedDbType,
  //     driverName: "",
  //     driverType: "",
  //   }));

  //   // Fetch driver names for the selected DB type
  //   const selectedDb: any = dbType.find(
  //     (db: any) => db.dbType === selectedDbType
  //   );
  //   console.log("This is inside select db", selectedDb);

  //   if (selectedDb && selectedDb.driverId) {
  //     setDriverNames(
  //       selectedDb.driverId.map((driver: any) => driver.driverName)
  //     );
  //     setDriverTypes([]); // Clear driver types as driverName changes
  //   } else {
  //     setDriverNames([]);
  //     setDriverTypes([]);
  //   }
  // };

  // const handleDriverNameChange = (event: any) => {
  //   const selectedDriverName = event.target.value;

  //   const selectedDriver: any = driverNames.find(
  //     (driver: any) => driver.driverName === selectedDriverName
  //   );

  //   setFormData((prev) => ({
  //     ...prev,
  //     driverName: selectedDriverName,
  //   }));
  // };

  const handleDbFamilyChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedFamilyName = event.target.value;
    const selectedFamily = dbHierarchyData.find(
      (f) => f.name === selectedFamilyName
    );

    setFilteredDbTypes(selectedFamily ? selectedFamily.dbTypes : []);
    setFilteredDriverNames([]); // Reset child

    setFormData((prev) => ({
      ...prev,
      dbFamily: selectedFamilyName,
      dbType: "", // Reset child selections
      driverName: "",
      driverType: "",
    }));
  };

  const handleDbTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedDbTypeName = event.target.value;
    const isExcel = selectedDbTypeName.toLowerCase() === "excel";
    const selectedDbType = filteredDbTypes.find(
      (t) => t.dbType === selectedDbTypeName
    );

    setFilteredDriverNames(selectedDbType ? selectedDbType.driverId : []);

    setFormData((prev) => ({
      ...prev,
      dbType: selectedDbTypeName,
      driverName: "",
      driverType: "",
      userName: prev.userName || (isExcel ? "dummy" : ""),
      pwd: prev.pwd || (isExcel ? "dummy" : ""),
    }));
  };

  const handleDriverNameChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedDriverName = event.target.value;
    const selectedDriver = filteredDriverNames.find(
      (d) => d.driverName === selectedDriverName
    );

    setFormData((prev) => ({
      ...prev,
      driverName: selectedDriverName,
      driverType: selectedDriver ? selectedDriver.driverType : "",
    }));
  };

  // const handleDbTypeChange = (event: any) => {
  //   const selectedDbType = event.target.value;
  //   const isExcel = selectedDbType.toLowerCase() === "excel";

  //   // Update `dbType` in the form data and reset dependent fields
  //   setFormData((prev) => ({
  //     ...prev,
  //     dbType: selectedDbType,
  //     driverName: "",
  //     driverType: "",
  //     userName: isExcel ? prev.userName || "dummy" : "",
  //     pwd: isExcel ? prev.pwd || "dummy" : "",
  //   }));

  //   // Fetch driver names and types for the selected DB type
  //   const selectedDb: any = dbType.find(
  //     (db: any) => db.dbType === selectedDbType
  //   );
  //   console.log("Selected DB Type:", selectedDb);

  //   if (selectedDb) {
  //     // Populate driver names
  //     setDriverNames(
  //       selectedDb.driverId.map((driver: any) => driver.driverName)
  //     );
  //     setDriverNameList(selectedDb.driverId);
  //     setDriverTypes([]);
  //   } else {
  //     // Reset if no data found
  //     setDriverNames([]);
  //     setDriverTypes([]);
  //   }
  // };

  // const handleDriverNameChange = (event: any) => {
  //   const selectedDriverName = event.target.value;

  //   // Find the driver object based on the selected driver name
  //   const selectedDrive: any = driverNameList?.find(
  //     (driver: any) => driver.driverName === selectedDriverName
  //   );

  //   setFormData((prev) => ({
  //     ...prev,
  //     driverName: selectedDriverName,
  //     driverType: selectedDrive?.driverType || "", // Set driver type if available
  //   }));
  // };
  const inputStyle =
    "form-control block w-full px-3 py-2 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded-md transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none";
  const selectStyle = "form-select " + inputStyle;
  const smallRadius = { borderRadius: "4px", borderColor: "#E0E0E0" };

  return (
    <div className="column">
      <div className="col-lg-12">
        <Card stretch style={{ borderRadius: "4px" }}>
          <CardBody>
            <div className="row">
              <div className="col-md-6 mb-4">
                <FormGroup label="Data Source Name">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  <Input
                    // fullWidth
                    // variant="outlined"
                    id="name"
                    name="name"
                    placeholder="Enter name of the Data Source"
                    value={formData.name}
                    onChange={handleChange}
                    style={smallRadius}
                    className={inputStyle}
                  />
                </FormGroup>
              </div>
              <div className="col-md-6 mb-4">
                <FormGroup label="URL">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  <Input
                    // fullWidth
                    // variant="outlined"
                    id="url"
                    name="url"
                    placeholder="Enter Url"
                    value={formData.url}
                    onChange={handleChange}
                    style={smallRadius}
                    className={inputStyle}
                  />
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <FormGroup label="user Name">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  <Input
                    // fullWidth
                    // variant="outlined"
                    id="userName"
                    name="userName"
                    placeholder="Enter User name"
                    value={formData.userName}
                    onChange={handleChange}
                    style={smallRadius}
                    className={inputStyle}
                  />
                </FormGroup>
              </div>
              <div className="col-md-6 mb-4">
                <FormGroup label="Password">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  <Input
                    // fullWidth
                    // variant="outlined"
                    type="password"
                    id="pwd"
                    name="pwd"
                    placeholder="Enter password"
                    value={formData.pwd}
                    onChange={handleChange}
                    autoComplete="new-password"
                    style={smallRadius}
                    className={inputStyle}
                  />
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <FormGroup label="DB Family">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  {/* <Select
                  // fullWidth
                  // variant="outlined"
                  id="db_type"
                  name="dbType"
                  ariaLabel="Select DB Family"
                  placeholder="Select DB Family"
                  value={formData.dbType}
                  onChange={handleDbTypeChange}
                >
                  <option value="" disabled>
                    Select a family...
                  </option>
                  {dbFamilies.map((family) => (
                    <option key={family} value={family}>
                      {family}
                    </option>
                  ))}
                </Select> */}
                  <Select
                    id="dbFamily"
                    name="dbFamily"
                    style={smallRadius}
                    className={selectStyle}
                    value={formData.dbFamily}
                    onChange={handleDbFamilyChange}
                    ariaLabel="Select DB Family"
                  >
                    <option value="" disabled>
                      Select a family...
                    </option>
                    {dbFamilies.map((family) => (
                      <option key={family} value={family}>
                        {family}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </div>
              <div className="col-md-6 mb-4">
                <FormGroup label="DB Type">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  {/* <Select
                  // fullWidth
                  // variant="outlined"
                  id="db_type"
                  name="dbType"
                  ariaLabel="Select DB Type"
                  placeholder="Enter DB Type"
                  value={formData.dbType}
                  onChange={handleDbTypeChange}
                >
                  {data.map((driver, index) => (
                    <option key={index} value={driver}>
                      {driver}
                    </option>
                  ))}
                </Select> */}
                  <Select
                    id="dbType"
                    name="dbType"
                    style={smallRadius}
                    className={selectStyle}
                    value={formData.dbType}
                    onChange={handleDbTypeChange}
                    disabled={!formData.dbFamily}
                    ariaLabel="Select DB Type"
                  >
                    <option value="" disabled>
                      Select a type...
                    </option>
                    {filteredDbTypes.map((type) => (
                      <option key={type.id} value={type.dbType}>
                        {type.dbType}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <FormGroup label="Driver Name">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  {/* <Select
                  // fullWidth
                  // variant="outlined"
                  id="driverName"
                  name="driverName"
                  ariaLabel="Select Driver Name"
                  placeholder="Enter Driver Name"
                  value={formData.driverName}
                  onChange={handleDriverNameChange}
                >
                  {driverNames?.map((driver, index) => (
                    <option key={index} value={driver}>
                      {driver}
                    </option>
                  ))}
                </Select> */}
                  <Select
                    id="driverName"
                    name="driverName"
                    style={smallRadius}
                    className={selectStyle}
                    value={formData.driverName}
                    onChange={handleDriverNameChange}
                    disabled={!formData.dbType}
                    ariaLabel="Select Driver Name"
                  >
                    <option value="" disabled>
                      Select a driver...
                    </option>
                    {filteredDriverNames.map((driver) => (
                      <option key={driver.id} value={driver.driverName}>
                        {driver.driverName}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </div>
              <div className="col-md-6 mb-4">
                <FormGroup label="Driver Type">
                  <span style={{ color: "red", marginRight: 4 }}>*</span>
                  <Select
                    id="driverType"
                    name="driverType"
                    style={smallRadius}
                    className={selectStyle}
                    ariaLabel="Select Driver Type"
                    placeholder="Select Driver Type"
                    value={formData.driverType}
                    onChange={handleChange}
                  >
                    <option>
                      {formData.driverType || "Select a Driver Name"}
                    </option>
                  </Select>
                </FormGroup>
              </div>
            </div>
          </CardBody>

          <CardFooter>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              {/* Wrap the buttons in a form */}
              <form onSubmit={handleSubmit(onSubmit)} style={{ width: "30%" }}>
                <div
                  className="column g-3"
                  style={{
                    display: "flex",
                    width: "100%",
                    gap: "4px",
                  }}
                >
                  <Button
                    isLight={true}
                    className="submit-reset"
                    style={{ border: "1px solid #d0d5dd" }}
                    type="button"
                    onClick={handleTestConnection}
                    isDisable={!isValid()}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.116 16C1.67133 16 1.291 15.8417 0.975 15.525C0.659 15.2083 0.500667 14.8287 0.5 14.386V1.615C0.5 1.171 0.658333 0.791 0.975 0.475C1.29167 0.159 1.67167 0.000666667 2.115 0H14.885C15.329 0 15.709 0.158333 16.025 0.475C16.341 0.791667 16.4993 1.17167 16.5 1.615V14.385C16.5 14.829 16.3417 15.209 16.025 15.525C15.7083 15.841 15.3283 15.9993 14.885 16H2.116ZM2.116 15H14.885C15.0643 15 15.2117 14.9423 15.327 14.827C15.4423 14.7117 15.5 14.5643 15.5 14.385V3H1.5V14.385C1.5 14.5643 1.55767 14.7117 1.673 14.827C1.78833 14.9423 1.936 15 2.116 15ZM8.5 12C7.41533 12 6.438 11.722 5.568 11.166C4.698 10.6107 4.04067 9.88867 3.596 9C4.04067 8.11133 4.698 7.38933 5.568 6.834C6.438 6.278 7.41533 6 8.5 6C9.58467 6 10.562 6.278 11.432 6.834C12.302 7.39 12.9593 8.112 13.404 9C12.9593 9.88867 12.302 10.6107 11.432 11.166C10.562 11.722 9.58467 12 8.5 12ZM8.496 10.116C8.18467 10.116 7.92133 10.007 7.706 9.789C7.492 9.571 7.385 9.30667 7.385 8.996C7.385 8.68467 7.49367 8.42133 7.711 8.206C7.929 7.992 8.19333 7.885 8.504 7.885C8.81533 7.885 9.07867 7.99367 9.294 8.211C9.50867 8.429 9.616 8.69333 9.616 9.004C9.616 9.31533 9.507 9.57867 9.289 9.794C9.071 10.0087 8.80667 10.116 8.496 10.116ZM8.5 10.885C9.02 10.885 9.46433 10.701 9.833 10.333C10.2017 9.965 10.3857 9.52067 10.385 9C10.3843 8.47933 10.2003 8.035 9.833 7.667C9.465 7.299 9.02067 7.115 8.5 7.115C7.97933 7.115 7.535 7.299 7.167 7.667C6.799 8.035 6.615 8.47933 6.615 9C6.615 9.52067 6.799 9.965 7.167 10.333C7.535 10.701 7.97933 10.885 8.5 10.885Z"
                          fill="#0D57DE"
                        />
                      </svg>
                      <span style={{ fontSize: "12px" }}>Test Connection</span>
                    </div>
                  </Button>
                  <Button
                    color="primary"
                    isLight={true}
                    className="submit-reset"
                    style={{ border: "1px solid #d0d5dd" }}
                    type="button"
                    onClick={handleReset}
                  >
                    <span style={{ fontSize: "12px" }}>Reset</span>
                  </Button>

                  <Button
                    color="primary"
                    isLight={true}
                    // className="m-1"
                    className="submit-button"
                    style={{ border: "1px solid #d0d5dd" }}
                    type="submit" // Use 'submit' type to trigger the form submit event
                    isDisable={!isValid()}
                  >
                    <span style={{ fontSize: "12px" }}>Submit</span>
                  </Button>
                </div>
              </form>
            </div>
          </CardFooter>
        </Card>
      </div>
      <div className="col-lg-12">
        <Card stretch style={{ borderRadius: "4px" }}>
          <CardBody>
            {show && (
              <div className="create-ds-test-connection">
                <Paper
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    border: "none",
                    boxShadow: "none",
                  }}
                  // elevation={3}
                >
                  <span
                    style={{
                      color: "#6f8bfb",
                      fontSize: "18px",
                      fontWeight: 400,
                    }}
                  >
                    {successMessage}
                  </span>
                </Paper>
              </div>
            )}
          </CardBody>
        </Card>
        <ToastComponent />
      </div>
    </div>
  );
}
